import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Bug, MessageSquare, Send, Sparkles } from 'lucide-react';
import type { ContactMessageCreateInput } from '@forest/contracts';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

export function BetaPage() {
  const { t } = useTranslation(['common']);
  const pushToast = useUiStore((s) => s.pushToast);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const submit = useMutation({
    mutationFn: (input: ContactMessageCreateInput) =>
      api.post('contact-messages', { json: input }).json(),
    onSuccess: () => {
      pushToast({
        variant: 'success',
        message: t('beta.contact.submitSuccess', {
          defaultValue: 'Mesajul a fost trimis. Vă vom răspunde în curând.',
        }),
      });
      setTitle('');
      setBody('');
      setOpen(false);
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const canSubmit = title.trim().length >= 3 && body.trim().length >= 10 && !submit.isPending;

  return (
    <div className="animate-fade-in mx-auto max-w-2xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900">
              {t('beta.title', { defaultValue: 'Versiune Beta' })}
            </h1>
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">
              Beta
            </span>
          </div>
          <p className="text-sm text-slate-500">
            {t('beta.subtitle', {
              defaultValue: 'Pnerp este în stadiu de testare',
            })}
          </p>
        </div>
      </div>

      <Card>
        <CardBody className="space-y-4 text-sm leading-relaxed text-slate-700">
          <p>
            {t('beta.intro', {
              defaultValue:
                'Această aplicație se află în versiune beta. Asta înseamnă că funcționalitățile principale sunt implementate și funcționale, dar aplicația este încă în curs de îmbunătățire.',
            })}
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <InfoCard
              icon={Sparkles}
              color="emerald"
              title={t('beta.working.title', {
                defaultValue: 'Ce funcționează',
              })}
              items={[
                t('beta.working.parcels', {
                  defaultValue: 'Registrul de poligoane forestiere',
                }),
                t('beta.working.photos', {
                  defaultValue: 'Baza de date cu fotografii georeferențiate',
                }),
                t('beta.working.import', {
                  defaultValue: 'Import din Excel și Google Drive',
                }),
                t('beta.working.map', {
                  defaultValue: 'Filtrare avansată și hartă interactivă',
                }),
                t('beta.working.mobile', {
                  defaultValue: 'Aplicația mobilă cu mod offline',
                }),
              ]}
            />

            <InfoCard
              icon={AlertTriangle}
              color="amber"
              title={t('beta.expect.title', {
                defaultValue: 'La ce să vă așteptați',
              })}
              items={[
                t('beta.expect.bugs', {
                  defaultValue: 'Posibile erori minore în interfață',
                }),
                t('beta.expect.changes', {
                  defaultValue: 'Funcționalități care se pot schimba',
                }),
                t('beta.expect.performance', {
                  defaultValue: 'Optimizări de performanță în curs',
                }),
                t('beta.expect.features', {
                  defaultValue: 'Funcționalități noi adăugate periodic',
                }),
              ]}
            />
          </div>

          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 dark:border-sky-900 dark:bg-sky-950/40">
            <div className="flex items-start gap-3">
              <Bug className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
              <div className="flex-1">
                <p className="font-semibold text-sky-900 dark:text-sky-200">
                  {t('beta.feedback.title', {
                    defaultValue: 'Ați găsit o problemă?',
                  })}
                </p>
                <p className="mt-0.5 text-xs text-sky-700 dark:text-sky-300">
                  {t('beta.feedback.body', {
                    defaultValue:
                      'Raportați orice eroare sau sugestie administratorului. Feedback-ul dumneavoastră ne ajută să îmbunătățim aplicația.',
                  })}
                </p>
              </div>
            </div>
            <div className="mt-3 flex justify-start sm:justify-end">
              <Button onClick={() => setOpen(true)}>
                <MessageSquare className="mr-1 h-4 w-4" />
                {t('beta.contact.button', {
                  defaultValue: 'Contactează Super Administratorul',
                })}
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
            <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
            <p className="text-xs text-slate-600 dark:text-slate-300">
              {t('beta.thanks', {
                defaultValue:
                  'Vă mulțumim că utilizați Pnerp în perioada beta. Contribuția dumneavoastră este esențială pentru dezvoltarea platformei.',
              })}
            </p>
          </div>
        </CardBody>
      </Card>

      <Dialog
        open={open}
        onClose={() => {
          if (!submit.isPending) setOpen(false);
        }}
        title={t('beta.contact.dialogTitle', {
          defaultValue: 'Contactează Super Administratorul',
        })}
        size="md"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit)
              submit.mutate({ category: 'OTHER', title: title.trim(), body: body.trim() });
          }}
        >
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('beta.contact.helpText', {
              defaultValue:
                'Mesajul ajunge direct la Super Administrator împreună cu numele și emailul dumneavoastră.',
            })}
          </p>
          <FormField
            label={t('beta.contact.titleLabel', { defaultValue: 'Titlu' })}
            htmlFor="contact-title"
          >
            <Input
              id="contact-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={t('beta.contact.titlePlaceholder', {
                defaultValue: 'Pe scurt, despre ce este vorba?',
              })}
              autoComplete="off"
              autoFocus
            />
          </FormField>
          <FormField
            label={t('beta.contact.bodyLabel', { defaultValue: 'Descriere' })}
            htmlFor="contact-body"
          >
            <textarea
              id="contact-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={5000}
              rows={6}
              placeholder={t('beta.contact.bodyPlaceholder', {
                defaultValue:
                  'Detalii: ce ați încercat să faceți, ce s-a întâmplat și ce v-ați așteptat.',
              })}
              className="w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={submit.isPending}
            >
              {t('beta.contact.cancel', { defaultValue: 'Anulează' })}
            </Button>
            <Button type="submit" loading={submit.isPending} disabled={!canSubmit}>
              <Send className="mr-1 h-4 w-4" />
              {t('beta.contact.submit', { defaultValue: 'Trimite' })}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

function InfoCard({
  icon: Icon,
  color,
  title,
  items,
}: {
  icon: React.ElementType;
  color: 'emerald' | 'amber';
  title: string;
  items: string[];
}) {
  const colors = {
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      icon: 'text-emerald-600',
      title: 'text-emerald-900',
      bullet: 'text-emerald-400',
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'text-amber-600',
      title: 'text-amber-900',
      bullet: 'text-amber-400',
    },
  }[color];

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-3`}>
      <div className="mb-2 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${colors.icon}`} />
        <h3 className={`text-xs font-bold uppercase tracking-wide ${colors.title}`}>{title}</h3>
      </div>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-1.5 text-xs text-slate-700">
            <span className={`mt-1 text-[8px] ${colors.bullet}`}>●</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
